import React, { useState, useEffect, useRef, useCallback } from 'react';
import AppLayout from '../components/AppLayout';
import api from '../api/axiosInstance';
import './StockSummary.css';

// Reusable clickable card header — navigates to a related page filtered by symbol
const CardHeader = ({ icon, title, badge, onNavigate }) => (
  <div
    className={`ss-card-header${onNavigate ? ' ss-card-header--clickable' : ''}`}
    role={onNavigate ? 'button' : undefined}
    tabIndex={onNavigate ? 0 : undefined}
    onClick={onNavigate}
    onKeyDown={onNavigate ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onNavigate(); } } : undefined}
    title={onNavigate ? 'Open in dedicated page' : undefined}
  >
    <span className="ss-card-icon">{icon}</span>
    <span className="ss-card-title">{title}</span>
    {badge != null && <span className="ss-card-badge">{badge}</span>}
    {onNavigate && <span className="ss-card-goto" aria-hidden="true">↗</span>}
  </div>
);

// ── Helpers ───────────────────────────────────────────────────────────────
const fmtNum = (v, decimals = 2) => (v == null ? '—' : Number(v).toFixed(decimals));
const fmtCr = (v) => {
  if (v == null) return '—';
  const n = Number(v);
  if (n >= 1e7) return `₹${(n / 1e7).toFixed(2)}L Cr`;
  if (n >= 1e5) return `₹${(n / 1e5).toFixed(2)}L`;
  return `₹${n.toFixed(0)}`;
};
const fmtDate = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};
const pctColor = (v) => {
  if (v == null) return '';
  return Number(v) >= 0 ? 'positive' : 'negative';
};

// ── Sub-components ────────────────────────────────────────────────────────

// Stock profile hero card
const ProfileCard = ({ info, summary, symbol }) => (
  <div className="ss-profile-card">
    <div className="ss-profile-main">
      <div className="ss-profile-symbol">{symbol}</div>
      <div className="ss-profile-details">
        <div className="ss-profile-name">{info?.stockName ?? 'Unknown Stock'}</div>
        <div className="ss-profile-meta">
          {info?.sector && String(info.sector).toLowerCase() !== 'unknown' && <span className="ss-badge ss-badge--sector">{info.sector}</span>}
          {(info?.categories ?? []).map((c) => (
            <span key={c} className="ss-badge ss-badge--cat">{String(c).toLowerCase() === 'others' ? 'CASH' : c}</span>
          ))}
          {info?.marketCap && <span className="ss-badge ss-badge--cap">{fmtCr(info.marketCap)}</span>}
        </div>
      </div>
    </div>
    <div className="ss-summary-pills">
      <SummaryPill label="Strategies" value={summary.totalStrategyHits} icon="🔬" active={summary.totalStrategyHits > 0} />
      <SummaryPill label="Sectors" value={summary.totalSectors} icon="🏭" active={summary.totalSectors > 0} />
      <SummaryPill label="Screeners" value={summary.totalScreeners} icon="📡" active={summary.totalScreeners > 0} />
      <SummaryPill label="Watchlists" value={summary.totalWatchlists} icon="📋" active={summary.totalWatchlists > 0} />
      <SummaryPill label="Nakshatra" value={summary.hasNakshatra ? '✓' : '—'} icon="⭐" active={summary.hasNakshatra} />
      <SummaryPill label="Market Data" value={summary.hasMarketData ? '✓' : '—'} icon="📈" active={summary.hasMarketData} />
      <SummaryPill label="CS Calendar" value={summary.hasCalendarData ? (summary.hasSpike ? '⚡ Spike' : '✓') : '—'} icon="📆" active={summary.hasCalendarData} />
    </div>
  </div>
);

const SummaryPill = ({ label, value, icon, active }) => (
  <div className={`ss-pill ${active ? 'ss-pill--active' : 'ss-pill--inactive'}`}>
    <span className="ss-pill-icon">{icon}</span>
    <span className="ss-pill-value">{value}</span>
    <span className="ss-pill-label">{label}</span>
  </div>
);

// ── Calendar Peak & Spike Card ──────────────────────────────────────────────
const MONTH_NAMES = ['','Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

const MiniBar = ({ value, maxAbs }) => {
  if (maxAbs === 0) return <span className="ss-bar-zero">—</span>;
  const pct = Math.min(100, (Math.abs(value) / maxAbs) * 100);
  return (
    <span className="ss-mini-bar-wrap">
      <span
        className={`ss-mini-bar ${value >= 0 ? 'bull' : 'bear'}`}
        style={{ width: `${pct}%` }}
      />
      <span className={`ss-mini-bar-val ${value >= 0 ? 'positive' : 'negative'}`}>
        {value >= 0 ? '+' : ''}{fmtNum(value, 1)}L
      </span>
    </span>
  );
};

const CalendarPeakCard = ({ calendarData, onNavigate }) => {
  const hasN50 = (calendarData || []).some((c) => c.collectionKey === 'nifty50');
  const filtered = (hasN50 ? calendarData.filter((c) => c.collectionKey === 'nifty50') : (calendarData || []))
    .map((c) => c.collectionKey === 'others' ? { ...c, collection: 'CASH' } : c);
  if (!filtered || filtered.length === 0) {
    return (
      <div className="ss-card">
        <CardHeader icon="📆" title="CS Calendar — Peak & Spike" onNavigate={onNavigate} />
        <div className="ss-empty">No calendar data found for this symbol</div>
      </div>
    );
  }

  return (
    <div className="ss-card ss-card--wide">
      <CardHeader
        icon="📆"
        title="CS Calendar — Peak & Spike"
        badge={`${filtered.length} collection${filtered.length !== 1 ? 's' : ''}`}
        onNavigate={onNavigate}
      />
      <div className="ss-cal-sections">
        {filtered.map((col) => (
          <div key={col.collectionKey} className="ss-cal-section">
            <div className="ss-cal-section-header">
              <span className="ss-badge ss-badge--col">{col.collection}</span>
              <span className="ss-cal-period">{MONTH_NAMES[col.month]} {col.year}</span>
              {col.currentMonth?.isSpike && (
                <span className={`ss-spike-badge ${col.currentMonth.isBullishSpike ? 'bull' : 'bear'}`}>
                  ⚡ {col.currentMonth.isBullishSpike ? 'Buying' : 'Selling'} Spike ×{fmtNum(Math.abs(col.currentMonth.spikeRatio), 1)}
                </span>
              )}
            </div>

            {col.currentMonth ? (
              <>
                {/* Month summary stats */}
                <div className="ss-cal-kv">
                  <div className="ss-cal-kv-item">
                    <span className="ss-kv-label">Month Total</span>
                    <span className={`ss-kv-value ${col.currentMonth.totalCsValue >= 0 ? 'positive' : 'negative'}`}>
                      {col.currentMonth.totalCsValue >= 0 ? '+' : ''}{fmtNum(col.currentMonth.totalCsValue, 1)}L
                    </span>
                  </div>
                  <div className="ss-cal-kv-item">
                    <span className="ss-kv-label">Daily Avg</span>
                    <span className={`ss-kv-value ${col.currentMonth.monthlyAvg >= 0 ? 'positive' : 'negative'}`}>
                      {col.currentMonth.monthlyAvg >= 0 ? '+' : ''}{fmtNum(col.currentMonth.monthlyAvg, 1)}L
                    </span>
                  </div>
                  <div className="ss-cal-kv-item">
                    <span className="ss-kv-label">Trading Days</span>
                    <span className="ss-kv-value">{col.currentMonth.tradingDays}</span>
                  </div>
                  {col.currentMonth.highestDay && (
                    <div className="ss-cal-kv-item">
                      <span className="ss-kv-label">🏔 Peak Day</span>
                      <span className="ss-kv-value positive">
                        {MONTH_NAMES[col.month]}-{String(col.currentMonth.highestDay.day).padStart(2,'0')}&nbsp;
                        (+{fmtNum(col.currentMonth.highestDay.csValue, 1)}L)
                      </span>
                    </div>
                  )}
                  {col.currentMonth.lowestDay && (
                    <div className="ss-cal-kv-item">
                      <span className="ss-kv-label">🕳 Low Day</span>
                      <span className="ss-kv-value negative">
                        {MONTH_NAMES[col.month]}-{String(col.currentMonth.lowestDay.day).padStart(2,'0')}&nbsp;
                        ({fmtNum(col.currentMonth.lowestDay.csValue, 1)}L)
                      </span>
                    </div>
                  )}
                  {col.currentMonth.latestDay && (
                    <div className="ss-cal-kv-item">
                      <span className="ss-kv-label">📅 Latest Day</span>
                      <span className={`ss-kv-value ${col.currentMonth.latestDay.csValue >= 0 ? 'positive' : 'negative'}`}>
                        {MONTH_NAMES[col.month]}-{String(col.currentMonth.latestDay.day).padStart(2,'0')}&nbsp;
                        ({col.currentMonth.latestDay.csValue >= 0 ? '+' : ''}{fmtNum(col.currentMonth.latestDay.csValue, 1)}L)
                      </span>
                    </div>
                  )}
                </div>

                {/* Daily bar chart */}
                {col.currentMonth.dailyData && col.currentMonth.dailyData.length > 0 && (() => {
                  const maxAbs = Math.max(...col.currentMonth.dailyData.map((d) => Math.abs(d.csValue)), 0.001);
                  return (
                    <div className="ss-daily-bars">
                      <div className="ss-daily-bars-label">Daily CS Activity This Month</div>
                      <div className="ss-daily-bars-grid">
                        {col.currentMonth.dailyData.map((d) => (
                          <div key={d.day} className="ss-daily-bar-row">
                            <span className="ss-daily-day">{String(d.day).padStart(2,'0')}</span>
                            <MiniBar value={d.csValue} maxAbs={maxAbs} />
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })()}
              </>
            ) : (
              <div className="ss-empty">No data for this month</div>
            )}

            {/* Year monthly trend */}
            {col.yearMonthly && col.yearMonthly.length > 0 && (() => {
              const maxAbs = Math.max(...col.yearMonthly.map((m) => Math.abs(m.totalCsValue)), 0.001);
              return (
                <div className="ss-year-trend">
                  <div className="ss-year-trend-label">{col.year} Monthly Trend</div>
                  <div className="ss-year-bars">
                    {col.yearMonthly.map((m) => (
                      <div key={m.month} className="ss-year-bar-item">
                        <div className="ss-year-bar-outer">
                          <div
                            className={`ss-year-bar-fill ${m.totalCsValue >= 0 ? 'bull' : 'bear'}`}
                            style={{ height: `${Math.min(100, (Math.abs(m.totalCsValue) / maxAbs) * 100)}%` }}
                          />
                        </div>
                        <span className="ss-year-bar-label">{m.label}</span>
                        <span className={`ss-year-bar-val ${m.totalCsValue >= 0 ? 'positive' : 'negative'}`}>
                          {m.totalCsValue >= 0 ? '+' : ''}{fmtNum(m.totalCsValue, 0)}L
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}
          </div>
        ))}
      </div>
    </div>
  );
};

// Strategy signals card
const StrategiesCard = ({ strategies, onNavigate }) => {
  const active = strategies.filter((s) => s.count > 0);
  const bullish = active.filter((s) => s.bullish);
  const bearish = active.filter((s) => !s.bullish);

  return (
    <div className="ss-card">
      <CardHeader
        icon="🔬"
        title="Strategy Signals"
        badge={`${active.length} / ${strategies.length}`}
        onNavigate={onNavigate}
      />
      {active.length === 0 ? (
        <div className="ss-empty">No strategy appearances found</div>
      ) : (
        <div className="ss-strategy-grid">
          {bullish.length > 0 && (
            <div className="ss-strategy-group">
              <div className="ss-strategy-group-label bullish">🟢 Bullish</div>
              {bullish.map((s) => <StrategyRow key={s.key} s={s} />)}
            </div>
          )}
          {bearish.length > 0 && (
            <div className="ss-strategy-group">
              <div className="ss-strategy-group-label bearish">🔴 Bearish</div>
              {bearish.map((s) => <StrategyRow key={s.key} s={s} />)}
            </div>
          )}
          {/* Show zero-count strategies in a collapsed section */}
          <div className="ss-strategy-inactive-label">Not Appeared</div>
          <div className="ss-strategy-inactive-row">
            {strategies.filter((s) => s.count === 0).map((s) => (
              <span key={s.key} className="ss-strategy-inactive-chip">{s.icon} {s.label}</span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const StrategyRow = ({ s }) => (
  <div className={`ss-strategy-row ${s.bullish ? 'bull' : 'bear'}`}>
    <span className="ss-strategy-icon">{s.icon}</span>
    <span className="ss-strategy-label">{s.label}</span>
    <span className="ss-strategy-count">{s.count} time{s.count !== 1 ? 's' : ''}</span>
    {s.latest && (
      <span className="ss-strategy-date">{s.latest.date}</span>
    )}
    {s.latest?.price != null && (
      <span className="ss-strategy-price">₹{fmtNum(s.latest.price)}</span>
    )}
    {s.latest?.percentageChange != null && (
      <span className={`ss-strategy-pct ${pctColor(s.latest.percentageChange)}`}>
        {Number(s.latest.percentageChange) >= 0 ? '+' : ''}{fmtNum(s.latest.percentageChange)}%
      </span>
    )}
  </div>
);

// Market CS Data card
const MarketDataCard = ({ marketData, onNavigate }) => {
  const hasN50 = marketData.some((r) => r.collection === 'NIFTY 50');
  const rows = (hasN50 ? marketData.filter((r) => r.collection === 'NIFTY 50') : marketData)
    .map((r) => r.collection === 'Others' ? { ...r, collection: 'CASH' } : r);
  return (
  <div className="ss-card">
    <CardHeader
      icon="📈"
      title="Market CS Data"
      badge={`${rows.length} collection${rows.length !== 1 ? 's' : ''}`}
      onNavigate={onNavigate}
    />
    {rows.length === 0 ? (
      <div className="ss-empty">No market CS data found</div>
    ) : (
      <div className="ss-table-wrap">
        <table className="ss-table">
          <thead>
            <tr>
              <th>Collection</th>
              <th>Date</th>
              <th>Price</th>
              <th>% Chg</th>
              <th>CS Value (L)</th>
              <th>ADU</th>
              <th>52W H/L</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={i}>
                <td><span className="ss-badge ss-badge--col">{row.collection}</span></td>
                <td>{fmtDate(row.date)}</td>
                <td>₹{fmtNum(row.iep)}</td>
                <td className={pctColor(row.pctChng)}>
                  {row.pctChng != null
                    ? `${Number(row.pctChng) >= 0 ? '+' : ''}${fmtNum(row.pctChng)}%`
                    : '—'}
                </td>
                <td className={row.csValue != null ? (Number(row.csValue) >= 0 ? 'positive' : 'negative') : ''}>
                  {row.csValue != null ? fmtNum(row.csValue) : '—'}
                </td>
                <td>
                  {row.adu ? (
                    <span className={`ss-adu ${row.adu.toLowerCase()}`}>{row.adu}</span>
                  ) : '—'}
                </td>
                <td className="ss-52w">
                  <span className="hi">H: {fmtNum(row.nm52WH)}</span>
                  <span className="lo">L: {fmtNum(row.nm52WL)}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </div>
  );
};

// Pre-Open card
const PreOpenCard = ({ preOpenData, onNavigate }) => {
  if (!preOpenData || !preOpenData.stock) {
    return (
      <div className="ss-card">
        <CardHeader icon="🌅" title="Pre-Open Data" onNavigate={onNavigate} />
        <div className="ss-empty">No pre-open data found</div>
      </div>
    );
  }
  const s = preOpenData.stock;
  return (
    <div className="ss-card">
      <CardHeader
        icon="🌅"
        title="Pre-Open Data"
        badge={`${preOpenData.category} · ${preOpenData.tradeDate}`}
        onNavigate={onNavigate}
      />
      <div className="ss-kv-grid">
        <KVItem label="IEP / Last Price" value={`₹${fmtNum(s.lastPrice ?? s.finalPrice)}`} />
        <KVItem label="Change" value={`${fmtNum(s.change)}`} cls={pctColor(s.change)} />
        <KVItem label="% Change" value={`${Number(s.pChange) >= 0 ? '+' : ''}${fmtNum(s.pChange)}%`} cls={pctColor(s.pChange)} />
        <KVItem label="Prev Close" value={`₹${fmtNum(s.previousClose)}`} />
        <KVItem label="52W High" value={`₹${fmtNum(s.yearHigh)}`} />
        <KVItem label="52W Low" value={`₹${fmtNum(s.yearLow)}`} />
        <KVItem label="Final Qty" value={s.totalTradedVolume != null ? Number(s.totalTradedVolume).toLocaleString('en-IN') : '—'} />
        <KVItem label="Turnover" value={s.finalValue != null ? `₹${(s.finalValue / 1e5).toLocaleString('en-IN', { maximumFractionDigits: 2 })} L` : '—'} />
        <KVItem label="FFM Mkt Cap" value={s.ffmMarketCap != null ? `₹${(s.ffmMarketCap / 1e7).toLocaleString('en-IN', { maximumFractionDigits: 0 })} Cr` : '—'} />
      </div>
    </div>
  );
};

const KVItem = ({ label, value, cls = '' }) => (
  <div className="ss-kv-item">
    <span className="ss-kv-label">{label}</span>
    <span className={`ss-kv-value ${cls}`}>{value}</span>
  </div>
);

// Nakshatra card
const NakshatraCard = ({ nakshatraData, onNavigate }) => (
  <div className="ss-card">
    <CardHeader
      icon="⭐"
      title="Astro Nakshatra"
      badge={`${nakshatraData.length} entr${nakshatraData.length !== 1 ? 'ies' : 'y'}`}
      onNavigate={onNavigate}
    />
    {nakshatraData.length === 0 ? (
      <div className="ss-empty">No Nakshatra data found</div>
    ) : (
      <div className="ss-nakshatra-list">
        {nakshatraData.map((n, i) => (
          <div key={i} className="ss-nakshatra-row">
            <span className="ss-nak-nak">✨ {n.nakshatra}</span>
            <span className="ss-nak-rashi">Rashi: {n.rashi}</span>
            <span className="ss-nak-sector">Sector: {n.nSector}</span>
            {n.nType && <span className="ss-nak-type">{n.nType}</span>}
          </div>
        ))}
      </div>
    )}
  </div>
);

// Sector membership card
const SectorCard = ({ sectorData, onNavigate }) => (
  <div className="ss-card">
    <CardHeader
      icon="🏭"
      title="Sector Membership"
      badge={`${sectorData.length} index${sectorData.length !== 1 ? 'es' : ''}`}
      onNavigate={onNavigate}
    />
    {sectorData.length === 0 ? (
      <div className="ss-empty">No sector data found</div>
    ) : (
      <div className="ss-sector-chips">
        {sectorData.map((s, i) => (
          <div key={i} className="ss-sector-chip">
            <span className="ss-sector-index">{s.indexName}</span>
            <span className="ss-sector-industry">{s.industry}</span>
            {s.series && <span className="ss-sector-series">{s.series}</span>}
          </div>
        ))}
      </div>
    )}
  </div>
);

// Chartink screener card
const ChartinkCard = ({ chartinkData, onNavigate }) => (
  <div className="ss-card">
    <CardHeader
      icon="📡"
      title="Strategy Insights"
      badge={`${chartinkData.length} screener${chartinkData.length !== 1 ? 's' : ''}`}
      onNavigate={onNavigate}
    />
    {chartinkData.length === 0 ? (
      <div className="ss-empty">Not found in any Chartink screener</div>
    ) : (
      <div className="ss-table-wrap">
        <table className="ss-table">
          <thead>
            <tr>
              <th>Screener</th>
              <th>Appearances</th>
              <th>Last Seen</th>
              <th>Close</th>
              <th>% Chg</th>
            </tr>
          </thead>
          <tbody>
            {chartinkData.map((c, i) => (
              <tr key={i}>
                <td className="ss-screener-name">{c.screenerName}</td>
                <td>{c.appearances}</td>
                <td>{fmtDate(c.lastSeen)}</td>
                <td>{c.latestClose != null ? `₹${fmtNum(c.latestClose)}` : '—'}</td>
                <td className={pctColor(c.latestPctChange)}>
                  {c.latestPctChange != null
                    ? `${Number(c.latestPctChange) >= 0 ? '+' : ''}${fmtNum(c.latestPctChange)}%`
                    : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </div>
);

// Watchlist card
const WatchlistCard = ({ watchlistData, onNavigate }) => (
  <div className="ss-card">
    <CardHeader
      icon="📋"
      title="Your Watchlists"
      badge={`${watchlistData.length} list${watchlistData.length !== 1 ? 's' : ''}`}
      onNavigate={onNavigate}
    />
    {watchlistData.length === 0 ? (
      <div className="ss-empty">Not in any of your watchlists</div>
    ) : (
      <div className="ss-watchlist-list">
        {watchlistData.map((wl, i) => (
          <div key={i} className="ss-watchlist-row">
            <span className="ss-wl-dot" style={{ background: wl.color || '#667eea' }}></span>
            <span className="ss-wl-name">{wl.watchlistName}</span>
            {wl.entryPrice != null && <span className="ss-wl-kv">Entry: ₹{fmtNum(wl.entryPrice)}</span>}
            {wl.targetPrice != null && <span className="ss-wl-kv positive">Target: ₹{fmtNum(wl.targetPrice)}</span>}
            {wl.stopLoss != null && <span className="ss-wl-kv negative">SL: ₹{fmtNum(wl.stopLoss)}</span>}
            {wl.tags.length > 0 && wl.tags.map((t) => <span key={t} className="ss-wl-tag">{t}</span>)}
            <span className="ss-wl-date">Added: {fmtDate(wl.addedAt)}</span>
          </div>
        ))}
      </div>
    )}
  </div>
);

// ── Main Page ─────────────────────────────────────────────────────────────
const openInNewTab = (path) => {
  window.open(path, '_blank', 'noopener,noreferrer');
};

// Pick a CS Calendar collection ('nifty50' | 'fo' | 'cash') for the given stock
const pickCalendarCollection = (calendarData, stockInfo) => {
  const keys = (calendarData || []).map((c) => c.collectionKey);
  if (keys.includes('nifty50')) return 'nifty50';
  if (keys.includes('fo')) return 'fo';
  if (keys.includes('cash') || keys.includes('others')) return 'cash';
  const cats = (stockInfo?.categories || []).map((c) => String(c).toLowerCase().replace(/\s|&/g, ''));
  if (cats.includes('nifty50')) return 'nifty50';
  if (cats.includes('fo') || cats.includes('fando')) return 'fo';
  if (cats.some((c) => c === 'others' || c === 'cash')) return 'cash';
  return 'nifty50';
};

const StockSummary = () => {
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedSymbol, setSelectedSymbol] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const searchRef = useRef(null);
  const debounceRef = useRef(null);

  // Autocomplete fetch
  const fetchSuggestions = useCallback(async (q) => {
    if (!q.trim()) { setSuggestions([]); return; }
    try {
      const res = await api.get('/stock-summary', { params: { q, limit: 12 } });
      setSuggestions(res.data.data || []);
    } catch {
      setSuggestions([]);
    }
  }, []);

  // Main data fetch
  const fetchSummary = useCallback(async (symbol) => {
    setLoading(true);
    setError(null);
    setData(null);
    try {
      const res = await api.get(`/stock-summary/${symbol}`);
      setData(res.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch stock summary');
    } finally {
      setLoading(false);
    }
  }, []);

  // Input handler with debounce
  const handleInput = (e) => {
    const val = e.target.value;
    setQuery(val);
    setShowSuggestions(true);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchSuggestions(val), 250);
  };

  const handleSelect = (stock) => {
    setQuery(stock.symbol);
    setSelectedSymbol(stock.symbol);
    setShowSuggestions(false);
    setSuggestions([]);
    fetchSummary(stock.symbol);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    const sym = query.trim().toUpperCase();
    if (!sym) return;
    setSelectedSymbol(sym);
    setShowSuggestions(false);
    fetchSummary(sym);
  };

  // Close suggestions on outside click
  useEffect(() => {
    const handler = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <AppLayout pageBadge="Stock Intelligence">
      <div className="ss-page">
        {/* ── Hero Search ─────────────────────────────────────────────── */}
        <div className="ss-hero">
          <div className="ss-hero-title">
            <span className="ss-hero-icon">🔍</span>
            <h1>Stock Intelligence Universe</h1>
          </div>
          <p className="ss-hero-sub">
            Search any symbol to see a complete site-wide summary — strategies, market data, sectors, astrology, screeners &amp; more.
          </p>
          <form className="ss-search-wrap" onSubmit={handleSearchSubmit} ref={searchRef}>
            <div className="ss-search-box">
              <span className="ss-search-icon">🔍</span>
              <input
                className="ss-search-input"
                type="text"
                placeholder="Search by symbol or company name…"
                value={query}
                onChange={handleInput}
                onFocus={() => query.trim() && setShowSuggestions(true)}
                autoComplete="off"
                spellCheck="false"
              />
              {query && (
                <button
                  type="button"
                  className="ss-search-clear"
                  onClick={() => { setQuery(''); setSuggestions([]); setData(null); setSelectedSymbol(null); }}
                  aria-label="Clear search"
                >✕</button>
              )}
              <button type="submit" className="ss-search-btn">Search</button>
            </div>

            {/* Autocomplete dropdown */}
            {showSuggestions && suggestions.length > 0 && (
              <div className="ss-suggestions">
                {suggestions.map((s) => (
                  <div
                    key={s.symbol}
                    className="ss-suggestion-item"
                    onMouseDown={() => handleSelect(s)}
                  >
                    <span className="ss-sug-symbol">{s.symbol}</span>
                    <span className="ss-sug-name">{s.stockName}</span>
                    <div className="ss-sug-meta">
                      {s.sector && <span className="ss-sug-sector">{s.sector}</span>}
                      {(s.categories || []).map((c) => (
                        <span key={c} className="ss-sug-cat">{String(c).toLowerCase() === 'others' ? 'CASH' : c}</span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </form>
        </div>

        {/* ── Loading ──────────────────────────────────────────────────── */}
        {loading && (
          <div className="ss-loading">
            <div className="ss-spinner"></div>
            <span>Fetching complete intelligence for <strong>{selectedSymbol}</strong>…</span>
          </div>
        )}

        {/* ── Error ────────────────────────────────────────────────────── */}
        {error && (
          <div className="ss-error">
            <span>⚠️ {error}</span>
          </div>
        )}

        {/* ── Results ──────────────────────────────────────────────────── */}
        {data && !loading && (
          <div className="ss-results">
            <ProfileCard
              info={data.stockInfo}
              summary={data.summary}
              symbol={data.symbol}
            />

            <div className="ss-cards-grid">
              {/* Always render in this order for layout consistency */}
              <StrategiesCard strategies={data.strategies} onNavigate={() => openInNewTab(`/strategy-radar?symbol=${encodeURIComponent(data.symbol)}`)} />
              <MarketDataCard marketData={data.marketData} onNavigate={() => openInNewTab(`/stock-insight?symbol=${encodeURIComponent(data.symbol)}`)} />
              <NakshatraCard nakshatraData={data.nakshatraData} onNavigate={() => openInNewTab(`/nakshatra?symbol=${encodeURIComponent(data.symbol)}`)} />
              <SectorCard sectorData={data.sectorData} onNavigate={() => openInNewTab(`/sector-radar?symbol=${encodeURIComponent(data.symbol)}`)} />
              <ChartinkCard chartinkData={data.chartinkData} onNavigate={() => openInNewTab(`/strategy-radar?symbol=${encodeURIComponent(data.symbol)}`)} />
              <CalendarPeakCard
                calendarData={data.calendarData}
                onNavigate={() => openInNewTab(
                  `/cs-calendar?symbol=${encodeURIComponent(data.symbol)}&collection=${pickCalendarCollection(data.calendarData, data.stockInfo)}`
                )}
              />
              <WatchlistCard watchlistData={data.watchlistData} onNavigate={() => openInNewTab(`/watchlist-tracker?symbol=${encodeURIComponent(data.symbol)}`)} />
            </div>
          </div>
        )}

        {/* ── Empty state (no search yet) ───────────────────────────────── */}
        {!data && !loading && !error && (
          <div className="ss-empty-state">
            <div className="ss-empty-state-icon">📊</div>
            <div className="ss-empty-state-text">
              Search for any stock symbol above to get a full intelligence report
            </div>
            <div className="ss-empty-state-hint">
              Aggregates data from: Strategies · Market CS Data · Sectors · Nakshatra · Chartink Screeners · Pre-Open · CS Calendar · Watchlists
            </div>
          </div>
        )}
      </div>
    </AppLayout>
  );
};

export default StockSummary;
